<script setup lang="ts">
import { formatNumberFullTruncate, middleTruncate } from '@/helpers/utils'
import BalanceCurrencyImage from '@/assets/images/dropee-token-new.webp'
import TonSymbol from '@/assets/images/ton-symbol.png'
import { useWalletConnect } from '@/composables/game/wallet-connect'
import BaseLinkButton from '@/components/base/BaseLinkButton.vue'
import { T, useTranslate } from '@tolgee/vue'
import router from '@/router'
import { RouteName } from '@/routes'
import CopyIcon from '@/components/icons/CopyIcon.vue'
import CheckCirclePinkBorder from '@/components/icons/CheckCirclePinkBorder.vue'
import { useClipboard } from '@/composables/helpers/clipboard'
import BaseButton from '@/components/base/BaseButton.vue'
import { computed, ref } from 'vue'
import { useFortuneWheel } from '@/composables/game/fortune-wheel'
import { useTonConnectModal } from '@townsquarelabs/ui-vue'
import WithdrawModal from '@/components/modals/WithdrawModal.vue'
import WithdrawOverlay from '@/components/overlays/WithdrawOverlay.vue'
import { env } from '@/env'
import webapp from '@/services/webapp'

const { userFriendlyAddress } = useWalletConnect()
const { copyToClipboard } = useClipboard()
const { t } = useTranslate()
const { currentBalance } = useFortuneWheel()
const { open } = useTonConnectModal()

function copyWalletAddress() {
  copyToClipboard(userFriendlyAddress.value, t.value('wallet.wallet_address_copied', 'Wallet address copied!'))
}

const isEnabled = computed(() => true)
const limitNotReached = computed(() => currentBalance.value < (env.app.isProd ? 100 : 1))

const showWithdrawModal = ref<boolean>(false)
const showWithdrawOverlay = ref<boolean>(false)

const withdrawAmount = ref<number>(currentBalance.value)

function onWithdrawClick() {
  showWithdrawModal.value = true
}

</script>
<template>
  <div class="flex flex-1 flex-col justify-between items-center w-full h-full">
    <div class="flex flex-col w-full gap-y-3">
      <div
        class="flex items-center py-[9px] pl-3 pr-4 bg-dark-bottom-layer w-full rounded-xl text-2xl font-semibold -tracking-[0.48px]"
      >
        <div class="flex items-center justify-between size-[42px] relative mr-3">
          <img
            :src="BalanceCurrencyImage"
            class="w-full object-contain"
          >
          <div class="size-3.5 absolute bottom-0 right-0">
            <img :src="TonSymbol">
          </div>
        </div>
        <span class="mr-2">{{ formatNumberFullTruncate(currentBalance, true) }}</span>
        <span class="text-white/60 uppercase">$DROPEE</span>
      </div>
      <div
        v-if="userFriendlyAddress"
        class="flex justify-between w-full py-[18px] px-4 bg-dark-top-layer rounded-xl"
      >
        <div class="flex items-center gap-x-2">
          <div class="text-base font-semibold">
            {{ middleTruncate(userFriendlyAddress, 11) }}
          </div>
          <CopyIcon
            class="w-4 h-4 min-w-[16px]"
            @click="copyWalletAddress"
          />
        </div>
        <div class="flex items-center gap-x-2">
          <CheckCirclePinkBorder
            wrapper-class="w-4 h-4 min-w-[16px] bg-[#32B8C4]"
            icon-class="w-2"
          />
          <div class="text-[#32B8C4] text-sm font-semibold">
            <T
              key-name="wallet.connected"
              default-value="Connected"
            />
          </div>
        </div>
      </div>
      <BaseLinkButton
        v-if="userFriendlyAddress"
        @click="router.push(RouteName.PROFILE)"
      >
        <T
          key-name="balance.manage_wallet"
          default-value="Manage wallet"
        />
      </BaseLinkButton>
    </div>
    <div class="flex flex-col w-full gap-y-3 flex-1 justify-end">
      <div
        class="flex items-center flex-col gap-y-3 w-full"
      >
        <BaseButton
          v-if="!userFriendlyAddress"
          class="w-full"
          @click="open"
        >
          <template
            #leftIcon
          >
            <img
              src="@/assets/images/gifs/money.gif"
              class="h-full w-auto max-w-[24px]"
            >
          </template>
          <T
            key-name="wallet.connect_wallet"
            default-value="Connect wallet"
          />
        </BaseButton>
        <BaseButton
          v-else
          style-type="secondary"
          class="w-full"
          :disabled="!isEnabled || limitNotReached"
          @click="onWithdrawClick"
        >
          <T
            key-name="balance.withdraw"
            default-value="Withdraw"
          />
        </BaseButton>
        <div
          v-if="isEnabled"
          class="text-center text-white/60 text-base font-medium"
        >
          Your tokens will be staked and visible at <BaseLinkButton @click="webapp.openTelegramLink('https://t.me/DropeeBot/token')">
            Dropee Token
          </BaseLinkButton>
        </div>
        <div
          v-if="isEnabled"
          class="text-center text-white/40 text-sm font-medium"
        >
          <T
            key-name="balance.withdraw.minimum"
            default-value="Minimum withdrawal: 100 $DROPEE."
          />
        </div>
        <div
          v-else
          class="text-center text-white/60 text-base font-medium"
        >
          <T
            key-name="balance.withdraw.explanation"
            default-value="Withdrawal available soon"
          />
        </div>
      </div>
    </div>
  </div>

  <WithdrawModal
    v-if="showWithdrawModal"
    :show="showWithdrawModal"
    @hide="showWithdrawModal = false"
    @after-confirm="showWithdrawOverlay = true"
  />
  <WithdrawOverlay
    v-if="showWithdrawOverlay"
    :class="showWithdrawOverlay ? 'z-[999] opacity-100' : '-z-[1] opacity-0'"
    :show="showWithdrawOverlay"
    :amount="withdrawAmount"
    :wallet-adress="userFriendlyAddress"
    @click="showWithdrawOverlay = false"
  />
</template>
